class ShagunHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-shagun-track]');
    this.slides = [...this.querySelectorAll('[data-shagun-slide]')];
    this.dots = [...this.querySelectorAll('[data-shagun-dot]')];
    this.index = 0;
    this.interval = Number(this.dataset.interval || 6000);
    this.autoplay = this.dataset.autoplay === 'true';
    this.visible = true;
    this.querySelector('[data-shagun-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
    this.querySelector('[data-shagun-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true)));
    this.addEventListener('mouseenter', this.stop);
    this.addEventListener('mouseleave', this.start);
    this.addEventListener('focusin', this.stop);
    this.addEventListener('focusout', this.start);
    this.track?.addEventListener('scroll', this.onScroll, { passive: true });
    this.track?.addEventListener('pointerdown', this.onPointerDown);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.visible ? this.start() : this.stop();
    }, { threshold: 0.35 });
    this.observer.observe(this);
    this.update();
    if (this.slides.length > 1) this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  show(nextIndex, userInitiated = false) {
    if (!this.slides.length || !this.track) return;
    this.index = (nextIndex + this.slides.length) % this.slides.length;
    const slide = this.slides[this.index];
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: userInitiated ? 'smooth' : 'smooth' });
    this.update();
    if (userInitiated) this.start();
  }

  update() {
    this.slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === this.index);
      slide.setAttribute('aria-current', index === this.index ? 'true' : 'false');
    });
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.index);
      dot.setAttribute('aria-current', index === this.index ? 'true' : 'false');
    });
  }

  onScroll = () => {
    window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      if (!this.track) return;
      const left = this.track.scrollLeft;
      let nearest = 0;
      let distance = Infinity;
      this.slides.forEach((slide, index) => {
        const value = Math.abs((slide.offsetLeft - this.track.offsetLeft) - left);
        if (value < distance) { distance = value; nearest = index; }
      });
      this.index = nearest;
      this.update();
    }, 70);
  };

  onPointerDown = (event) => {
    if (event.pointerType === 'touch' || !this.track) return;
    const startX = event.clientX;
    const startLeft = this.track.scrollLeft;
    let moved = false;
    const move = (e) => {
      const delta = e.clientX - startX;
      if (Math.abs(delta) > 5) moved = true;
      this.track.scrollLeft = startLeft - delta;
    };
    const up = (e) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      this.track.classList.remove('is-dragging');
      if (moved) { e.preventDefault(); this.start(); }
    };
    this.stop();
    this.track.classList.add('is-dragging');
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  };

  onVisibilityChange = () => document.hidden ? this.stop() : this.start();

  onBlockSelect = (event) => {
    const slide = event.target?.closest?.('[data-shagun-slide]');
    if (!slide || !this.contains(slide)) return;
    this.stop();
    this.show(this.slides.indexOf(slide), true);
  };

  start = () => {
    this.stop();
    if (!this.autoplay || !this.visible || document.hidden || this.slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.Shopify?.designMode) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  };

  stop = () => {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  };
}

if (!customElements.get('shagun-hero')) customElements.define('shagun-hero', ShagunHero);

class ShagunReels extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-reels-track]');
    this.querySelector('[data-reels-prev]')?.addEventListener('click', () => this.move(-1));
    this.querySelector('[data-reels-next]')?.addEventListener('click', () => this.move(1));
    this.querySelectorAll('[data-reel-play]').forEach((button) => button.addEventListener('click', () => this.toggle(button.closest('[data-reel-card]'))));
    this.querySelectorAll('video').forEach((video) => {
      video.addEventListener('ended', () => video.closest('[data-reel-card]')?.classList.remove('is-playing'));
    });
  }
  move(direction) {
    const card = this.querySelector('[data-reel-card]');
    if (!this.track || !card) return;
    this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + 16) * 2, behavior: 'smooth' });
  }
  toggle(card) {
    const video = card?.querySelector('video');
    if (!video) {
      const link = card?.querySelector('a[href*="instagram.com"]');
      if (link) window.open(link.href, '_blank', 'noopener');
      return;
    }
    this.querySelectorAll('video').forEach((item) => { if (item !== video) { item.pause(); item.closest('[data-reel-card]')?.classList.remove('is-playing'); } });
    if (video.paused) { video.play(); card.classList.add('is-playing'); } else { video.pause(); card.classList.remove('is-playing'); }
  }
}
if (!customElements.get('shagun-reels')) customElements.define('shagun-reels', ShagunReels);
